/**
 * Author: Claude Code
 * Version: v3.2.157
 * Modified: 2025-10-21
 *
 * LoadingScreen - Event-Driven Loading UI
 * 监听后端game:loading事件实时更新进度，替代硬编码定时器
 */

import { useEffect, useState } from 'react';
import webSocketService from '../services/websocket/WebSocketService';
import progressAggregator from '../services/progress/ProgressAggregator';

interface LoadingStage {
  stage: string;
  stageIndex: number;
  startPercentage: number;
  endPercentage: number;
  message: string;
}

const LOADING_STAGES: LoadingStage[] = [
  { stage: 'room_init', stageIndex: 1, startPercentage: 0, endPercentage: 20, message: 'Initializing room dependencies...' },
  { stage: 'game_init', stageIndex: 2, startPercentage: 20, endPercentage: 40, message: 'Creating game engine...' },
  { stage: 'agent_registration', stageIndex: 3, startPercentage: 40, endPercentage: 60, message: 'Registering agents...' },
  { stage: 'map_generation', stageIndex: 4, startPercentage: 60, endPercentage: 80, message: 'Generating game map...' },
  { stage: 'asset_preload', stageIndex: 5, startPercentage: 80, endPercentage: 100, message: 'Preloading assets...' }
];

interface LoadingScreenProps {
  onComplete?: () => void;
}

export default function LoadingScreen({ onComplete }: LoadingScreenProps) {
  const [percentage, setPercentage] = useState(0);
  const [message, setMessage] = useState('Preparing to load...');
  const [isComplete, setIsComplete] = useState(false);

  // 🆕 v3.2.134: Event-driven loading progress（统一使用 UPPER_SNAKE_CASE 内部事件）
  useEffect(() => {
    // Helper: finalize loading and trigger callback once
    const completeNow = (finalMessage?: string) => {
      if (isComplete) return;
      setPercentage(100);
      if (finalMessage) setMessage(finalMessage);
      setIsComplete(true);
      setTimeout(() => { onComplete?.(); }, 500);
    };

    // Handler for game:loading:started event
    const handleLoadingStarted = (data: any) => {
      console.log('🎮 Loading started:', data);
      try { progressAggregator.reset(); } catch {}
      setPercentage(0);
      setMessage('Preparing to load...');
    };

    // Handler for game:loading:progress event
    const handleLoadingProgress = (data: any) => {
      console.log('📊 Loading progress:', data);
      const { percentage, message } = data || {};
      try { progressAggregator.setServerProgress(Math.floor(percentage || 0)); } catch {}
      setPercentage(progressAggregator.getCombined());
      setMessage(message || 'Loading...');
    };

    // Handler for game:loading:completed event
    const handleLoadingCompleted = (data: any) => {
      console.log('✅ Loading completed:', data);
      const { totalDuration } = data || { totalDuration: 0 };
      completeNow(`Loaded in ${(totalDuration / 1000).toFixed(1)}s - Starting game...`);
    };

    // NEW: Close loading on first authoritative data as well
    const handleSystemStateSync = (_snapshot: any) => {
      console.log('🟢 SYSTEM_STATE_SYNC received → close loading');
      completeNow('Snapshot received - Starting game...');
    };

    const handleMapGenerated = (_payload: any) => {
      console.log('🗺️ MAP_GENERATED received → close loading');
      completeNow('Map ready - Starting game...');
    };

    // Register event listeners
    webSocketService.on('GAME_LOADING_STARTED', handleLoadingStarted);
    webSocketService.on('GAME_LOADING_PROGRESS', handleLoadingProgress);
    webSocketService.on('GAME_LOADING_COMPLETED', handleLoadingCompleted);
    // Authoritative events also close loading
    webSocketService.on('SYSTEM_STATE_SYNC', handleSystemStateSync);
    webSocketService.on('MAP_GENERATED', handleMapGenerated);

    // Subscribe aggregator for client-side progress and combined updates
    const unsubscribe = progressAggregator.subscribe((p) => { if (!isComplete) setPercentage(p); });

    // Cleanup event listeners on unmount
    return () => {
      webSocketService.off('GAME_LOADING_STARTED', handleLoadingStarted);
      webSocketService.off('GAME_LOADING_PROGRESS', handleLoadingProgress);
      webSocketService.off('GAME_LOADING_COMPLETED', handleLoadingCompleted);
      webSocketService.off('SYSTEM_STATE_SYNC', handleSystemStateSync);
      webSocketService.off('MAP_GENERATED', handleMapGenerated);
      unsubscribe?.();
    };
  }, [onComplete, isComplete]);

  // 根据进度决定颜色
  const getProgressColor = () => {
    if (percentage >= 80) return '#4ade80'; // green-500
    if (percentage >= 40) return '#60a5fa'; // blue-400
    return '#4a9eff'; // blue-600
  };

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      background: '#1a1a2e',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }}>
      <div className="hero-content">
        {/* 标题 - 使用项目hero-title样式 */}
        <h1 className="hero-title" style={{ marginBottom: 'var(--space-12)' }}>
          Agent Battle Royale 3.0
        </h1>

        {/* 副标题 - 使用项目hero-subtitle样式 */}
        <p className="hero-subtitle" style={{ marginBottom: 'var(--space-16)' }}>
          Loading Game...
        </p>

        {/* 进度条容器 - 固定宽度600px，居中 */}
        <div style={{
          position: 'relative',
          width: '600px',
          marginBottom: 'var(--space-8)',
          marginLeft: 'auto',
          marginRight: 'auto'
        }}>
          {/* 进度条背景 */}
          <div style={{
            height: '40px',
            background: '#222222',
            border: '2px solid #444444',
            borderRadius: 'var(--radius-lg)',
            boxShadow: '0 4px 6px rgba(0,0,0,0.3)',
            overflow: 'hidden'
          }}>
            {/* 进度条填充 */}
            <div style={{
              height: '100%',
              width: `${percentage}%`,
              background: getProgressColor(),
              transition: 'width 0.1s ease-linear'
            }} />
          </div>

          {/* 百分比文字（居中在进度条内） */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 'var(--font-3xl)',
            fontWeight: 'var(--font-bold)',
            color: 'var(--color-text-primary)',
            textShadow: '0 2px 4px rgba(0,0,0,0.8)',
            pointerEvents: 'none'
          }}>
            {percentage}%
          </div>
        </div>

        {/* 加载消息 */}
        <p style={{
          fontSize: 'var(--font-lg)',
          color: 'var(--color-text-secondary)',
          textAlign: 'center',
          minHeight: '28px'
        }}>
          {message}
        </p>
      </div>
    </div>
  );
}
